import { useQuery } from "@tanstack/react-query";
import { Navigate, useLocation } from "react-router";
import { AppLayout } from "./AppLayout";
import { LoadingBlock } from "./ui";
import { NotSignedIn, api } from "../lib/api";
import { keys } from "../lib/queries";
import type { Affiliate, Program } from "../lib/types";
import { useTranslation } from "react-i18next";

/**
 * The gate, and the only place this app asks "am I signed in".
 *
 * There is no auth provider and no session context here, which is where this
 * departs from both other dashboards — and the reason is that there is no token
 * to hold. The session is an httpOnly cookie the browser attaches on its own,
 * so "who am I" is an ordinary query with an ordinary cache rather than state
 * something has to keep in step with a refresh cycle.
 *
 * `retry: false` on a 401 matters more than it looks. TanStack Query retries a
 * failed query three times by default, so without this a signed-out visitor
 * waits through three round trips and a backoff before being sent to the
 * sign-in page — which reads as the site being slow rather than as them being
 * signed out.
 */
export function RequireSession() {
  const location = useLocation();
  const { t } = useTranslation("common");

  const { data, isPending, error } = useQuery({
    queryKey: keys.me(),
    queryFn: () => api<{ affiliate: Affiliate; program: Program }>("/v1/affiliate/me"),
    retry: (failureCount, cause) => !(cause instanceof NotSignedIn) && failureCount < 2,
    // Re-asked when the tab is focused again, so a session revoked from another
    // device does not leave a dashboard that still looks signed in.
    refetchOnWindowFocus: true,
  });

  if (isPending) return <LoadingBlock label={t("signingIn")} />;

  if (error instanceof NotSignedIn) {
    /*
     * Where they were going, carried through so signing in lands there. Encoded
     * once here rather than assembled in `signInUrl`, because this is the only
     * caller that has a location to carry and the sign-in page's own "start
     * again" link deliberately does not.
     */
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?return_to=${encodeURIComponent(returnTo)}`} replace />;
  }

  /*
   * Any other failure is the service being unreachable, and it is *not* treated
   * as being signed out. Sending somebody to a sign-in page because an API is
   * down produces a loop: they sign in, come back, and get sent away again.
   */
  if (error) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center text-[13px] text-fg-muted">
        {error.message}
      </div>
    );
  }

  return (
    <AppLayout displayName={data.affiliate.displayName} role={data.affiliate.role} />
  );
}
